import { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Check, Clock, Plane, X } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import {
  STATUS_CLASSES,
  dayLabel,
  fmtDate,
  fmtDays,
  fmtRange,
  todayIso,
  type MyPtoResponse,
  type PtoBalance,
  type PtoPreview,
  type PtoRequest,
} from '../pto/types.ts';

export function PtoPage(): React.ReactElement {
  useDocumentTitle('My PTO');
  const { agent } = useAuth();
  const toast = useToast();
  const [data, setData] = useState<MyPtoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch<MyPtoResponse>('/api/pto/me'));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load PTO.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function cancel(id: string): Promise<void> {
    try {
      await apiFetch(`/api/pto/requests/${id}/cancel`, { method: 'POST', json: {} });
      toast.success('Request cancelled — the days are back in your balance.');
      await load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not cancel that request.');
    }
  }

  if (!agent) return <p>Loading…</p>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[var(--color-header)]">My PTO</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          You earn <strong>one day per month</strong> from your start date. The balance resets on
          your work anniversary — <strong>unused days don't roll over</strong> — and you can take
          up to <strong>{data?.balance.maxDaysPerRun ?? 5} days at a time</strong>, once they've
          been earned. Weekends and company holidays inside a request are free.
        </p>
      </div>

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      {data && <BalanceCard balance={data.balance} />}

      {data?.balance.eligible && <RequestForm onFiled={load} />}

      {data && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[var(--color-header)]">My requests</h2>
          {data.requests.length === 0 ? (
            <Card>
              <p className="text-sm text-[var(--color-muted)]">
                You haven't requested any time off yet.
              </p>
            </Card>
          ) : (
            <Card className="!p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-[var(--color-border)] bg-white text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                    <tr>
                      <th className="px-4 py-2.5">Dates</th>
                      <th className="px-4 py-2.5">Days</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5">Note</th>
                      <th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.requests.map((r) => (
                      <RequestRow key={r.id} request={r} onCancel={() => void cancel(r.id)} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </section>
      )}

      {data && data.adjustments.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[var(--color-header)]">Adjustments</h2>
          <Card className="!p-0 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {data.adjustments.map((a) => (
                  <tr key={a.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="px-4 py-2.5">{fmtDate(a.effectiveOn)}</td>
                    <td className="px-4 py-2.5 font-semibold">
                      {a.days > 0 ? '+' : ''}
                      {fmtDays(a.days)}
                    </td>
                    <td className="px-4 py-2.5 text-[var(--color-muted)]">{a.note}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-[var(--color-muted)]">
                      {a.createdByName ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </section>
      )}
    </div>
  );
}

function BalanceCard({ balance }: { balance: PtoBalance }): React.ReactElement {
  if (!balance.eligible) {
    return (
      <Card className="border-amber-300 bg-amber-50/50">
        <h2 className="text-base font-semibold text-[var(--color-header)]">No PTO balance</h2>
        <p className="mt-1 text-sm text-[var(--color-body)]">
          {balance.ineligibleReason === 'part_time'
            ? 'PTO is accrued by full-time staff only.'
            : 'Your start date isn’t on file yet, so accrual can’t be calculated. An admin can add it on the Agents page.'}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">
            PTO year {balance.yearStart && fmtDate(balance.yearStart)} –{' '}
            {balance.yearEnd && fmtDate(addDaysIso(balance.yearEnd, -1))}
          </h2>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Year {(balance.yearIndex ?? 0) + 1} of service · started{' '}
            {balance.hiredOn && fmtDate(balance.hiredOn)}
          </p>
        </div>
        {balance.nextAccrualOn && (
          <div className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-xs text-[var(--color-muted)]">
            <Clock className="mr-1 inline h-3.5 w-3.5" />
            Next day earned {fmtDate(balance.nextAccrualOn)}
          </div>
        )}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Metric label="Available now" value={fmtDays(balance.availableDays)} big />
        <Metric label="Earned this year" value={fmtDays(balance.accruedDays)} />
        <Metric label="Taken" value={fmtDays(balance.usedDays)} />
        <Metric
          label="Awaiting approval"
          value={fmtDays(balance.pendingDays)}
          muted={balance.pendingDays === 0}
        />
      </div>
      {balance.adjustmentDays !== 0 && (
        <p className="mt-3 text-xs text-[var(--color-muted)]">
          Includes an admin adjustment of {balance.adjustmentDays > 0 ? '+' : ''}
          {fmtDays(balance.adjustmentDays)} days this year.
        </p>
      )}
      <p className="mt-3 text-xs text-[var(--color-muted)]">
        Anything left over on {balance.yearEnd && fmtDate(balance.yearEnd)} is lost — the new year
        starts at 1 day.
      </p>
    </Card>
  );
}

function Metric({
  label,
  value,
  big,
  muted,
}: {
  label: string;
  value: string;
  big?: boolean;
  muted?: boolean;
}): React.ReactElement {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</div>
      <div
        className={[
          big ? 'text-3xl' : 'text-2xl',
          'font-semibold',
          muted ? 'text-[var(--color-muted)]' : 'text-[var(--color-header)]',
        ].join(' ')}
      >
        {value}
      </div>
    </div>
  );
}

function RequestForm({ onFiled }: { onFiled: () => Promise<void> }): React.ReactElement {
  const toast = useToast();
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [firstDayHalf, setFirstDayHalf] = useState(false);
  const [lastDayHalf, setLastDayHalf] = useState(false);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<PtoPreview | null>(null);
  const [saving, setSaving] = useState(false);

  const singleDay = startDate === endDate;

  // Keep the end date at or after the start date as the user moves it.
  useEffect(() => {
    if (endDate < startDate) setEndDate(startDate);
  }, [startDate, endDate]);

  // Ask the server what the range actually costs — it owns the holiday
  // calendar and the policy, so the form never has to guess.
  useEffect(() => {
    let cancelled = false;
    if (!startDate || !endDate || endDate < startDate) {
      setPreview(null);
      return;
    }
    const params = new URLSearchParams({
      startDate,
      endDate,
      firstDayHalf: String(firstDayHalf),
      lastDayHalf: String(singleDay ? firstDayHalf : lastDayHalf),
    });
    apiFetch<PtoPreview>(`/api/pto/preview?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setPreview(r);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, firstDayHalf, lastDayHalf, singleDay]);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/api/pto/requests', {
        method: 'POST',
        json: {
          startDate,
          endDate,
          firstDayHalf,
          lastDayHalf: singleDay ? firstDayHalf : lastDayHalf,
          reason: reason.trim() || undefined,
        },
      });
      toast.success('Request sent — an admin will approve or deny it.');
      setReason('');
      setFirstDayHalf(false);
      setLastDayHalf(false);
      await onFiled();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not file that request.');
    } finally {
      setSaving(false);
    }
  }

  const blocked = preview?.problem ?? null;

  return (
    <Card>
      <h2 className="text-base font-semibold text-[var(--color-header)]">
        <Plane className="mr-2 inline h-4 w-4" />
        Request time off
      </h2>
      <form onSubmit={(e) => void submit(e)} className="mt-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="First day"
            type="date"
            name="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
          <FormField
            label="Last day"
            type="date"
            name="endDate"
            min={startDate}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
            <input
              type="checkbox"
              checked={firstDayHalf}
              onChange={(e) => setFirstDayHalf(e.target.checked)}
              className="h-4 w-4 rounded border-[var(--color-border)]"
            />
            {singleDay ? 'Half day' : 'First day is a half day'}
          </label>
          {!singleDay && (
            <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
              <input
                type="checkbox"
                checked={lastDayHalf}
                onChange={(e) => setLastDayHalf(e.target.checked)}
                className="h-4 w-4 rounded border-[var(--color-border)]"
              />
              Last day is a half day
            </label>
          )}
        </div>

        <FormField
          label="Reason (optional)"
          name="reason"
          value={reason}
          maxLength={500}
          placeholder="Family trip, appointment…"
          onChange={(e) => setReason(e.target.value)}
        />

        {preview && (
          <div
            className={[
              'rounded-[var(--radius-button)] border px-3 py-2.5 text-sm',
              blocked
                ? 'border-[var(--color-danger)]/40 bg-red-50/60 text-[var(--color-danger)]'
                : 'border-[var(--color-border)] bg-white text-[var(--color-body)]',
            ].join(' ')}
          >
            <CalendarDays className="mr-1.5 inline h-4 w-4" />
            {blocked ? blocked.message : `This costs ${dayLabel(preview.days)}.`}
          </div>
        )}

        <Button type="submit" disabled={saving || !preview || Boolean(blocked)}>
          {saving ? 'Sending…' : 'Send request'}
        </Button>
      </form>
    </Card>
  );
}

function RequestRow({
  request,
  onCancel,
}: {
  request: PtoRequest;
  onCancel: () => void;
}): React.ReactElement {
  const cancellable = request.status === 'pending' || request.status === 'approved';
  return (
    <tr className="border-b border-[var(--color-border)] last:border-0">
      <td className="px-4 py-2.5">
        <div className="font-medium text-[var(--color-header)]">{fmtRange(request)}</div>
        {(request.firstDayHalf || request.lastDayHalf) && (
          <div className="text-xs text-[var(--color-muted)]">
            {request.startDate === request.endDate
              ? 'Half day'
              : [
                  request.firstDayHalf ? 'first day half' : null,
                  request.lastDayHalf ? 'last day half' : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">{fmtDays(request.days)}</td>
      <td className="px-4 py-2.5">
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium capitalize ${STATUS_CLASSES[request.status]}`}
        >
          {request.status === 'approved' && <Check className="mr-1 h-3 w-3" />}
          {request.status === 'denied' && <X className="mr-1 h-3 w-3" />}
          {request.status}
        </span>
      </td>
      <td className="px-4 py-2.5 text-xs text-[var(--color-muted)]">
        {request.decisionNote ?? request.reason ?? '—'}
        {request.decidedByName && (
          <div className="text-[11px]">
            {request.status === 'cancelled' ? 'by' : 'decided by'} {request.decidedByName}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        {cancellable && (
          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-semibold text-[var(--color-danger)] hover:underline"
          >
            Cancel
          </button>
        )}
      </td>
    </tr>
  );
}

// The API returns the year end as an exclusive bound; showing it as an
// inclusive last day reads better.
function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y!, (m ?? 1) - 1, (d ?? 1) + days));
  return date.toISOString().slice(0, 10);
}
